import { useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { CustomGPT } from "@/api/models";
import { doTrackEvent } from "@/utils/utils";
import CustomGPTListItem from "@/components/Sidebar/CustomGPTListItem";

const containerVariants = {
  collapsed: { height: 0, opacity: 0, transition: { when: "afterChildren" } },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      when: "beforeChildren",
    },
  },
};

const itemVariants = {
  collapsed: { opacity: 0, y: -10 },
  expanded: { opacity: 1, y: 0 },
};

const CustomGPTList = () => {
  const appStateContext = useContext(AppStateContext);
    const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [customGPTs, setCustomGPTs] = useState<CustomGPT[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  /** 
   * Handle setting initial expanded state based on localStorage
  **/
  useEffect(() => {
    if (localStorage.getItem("sms-gpt_cgpt-expanded") === "1") setExpanded(true);
  }, []);

  /** 
   * Handle changing loading state when chat history is loaded
  **/
  useEffect(() => {
    if (appStateContext?.state.customGPTs !== null) {
      setCustomGPTs(appStateContext?.state.customGPTs || []);
      setLoading(false);
    }
  }, [appStateContext?.state.customGPTs]);

  /** 
   * Handle toggling the expanded state of the list
  **/
  const toggleExpanded = () => {
    const newValue = !expanded;
    setExpanded(newValue);
    localStorage.setItem("sms-gpt_cgpt-expanded", newValue ? "1" : "0");
  };

  if (loading) return null;

  return (
    <div className="text-[13px] px-1">
      <button type="button" className="btn btn-xs btn-ghost justify-start items-center w-full text-black/50 dark:text-white/50 text-xs p-0 mt-4" onClick={() => { toggleExpanded(); handleTrackEvent('click', 'cgpt_toggle', 'Custom GPTs Toggle', '', '', ''); }}>
        Spaces
        {expanded ? <ChevronDown size={14} className="mt-0.5" /> : <ChevronRight size={14} className="mt-0.5" /> }
      </button>

      <motion.div
        initial="collapsed"
        animate={expanded ? "expanded" : "collapsed"}
        variants={containerVariants}
        style={{ overflow: "hidden" }}
      >
        <AnimatePresence>
          {customGPTs.slice(0, 5).map((customGPT, idx) => (
            <motion.div
              key={customGPT.id ?? idx}
              variants={itemVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
            >
              <CustomGPTListItem customGPT={customGPT} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default CustomGPTList;
